import { AxiosError, AxiosResponse } from 'axios';
import { Button } from 'components/universal/button';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { toast } from 'react-toastify';
import { generateLoginAndPasswordAxsios } from 'services/registration';
import { APIErrors } from 'types/api';
import { employeesInterface } from 'types/employee';
import cls from './generateLoginForm.module.scss';

export interface sendLoginsAndPasswordsValue {
    userIds: number[];
}

interface GenerateLoginFormInterface {
    users: employeesInterface[];
    onCancel: (boolean) => void;
}

export const GenerateLoginForm = ({
    users,
    onCancel
}: GenerateLoginFormInterface) => {
    const { t } = useTranslation();
    const [userNames, setUserNames] = useState<string>('');

    const {
        register,
        handleSubmit,
        setError,
        setValue,
        formState: { errors },
    } = useForm<sendLoginsAndPasswordsValue>({
        defaultValues: {
            userIds: [],
        },
    });

    useEffect(() => {
        const userIdsString = users
            .map(user => user.id)
            .filter(id => id != null);

        const userNamesString = users
            .map(user => {
                const fullName = `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim();
                return fullName !== '' ? fullName : user.login;
            })
            .filter(name => name) 
            .join(', ');

        setUserNames(userNamesString);
        setValue('userIds', userIdsString);
    }, [users, setValue]);

    const onSubmit = (formObject: sendLoginsAndPasswordsValue) => {
        generateLoginAndPassword.mutate(formObject);
    };

    const generateLoginAndPassword = useMutation<
        AxiosResponse<Blob>,
        AxiosError<APIErrors>,
        sendLoginsAndPasswordsValue
    >((data) => generateLoginAndPasswordAxsios(data), {
        onSuccess: (data) => {
            
            if (data.status === 200) {
                const url = window.URL.createObjectURL(data.data);
                const a = document.createElement('a');
                a.href = url;
                a.download = 'login-and-password.pdf';
                document.body.appendChild(a);
                a.click();
                a.remove();
                window.URL.revokeObjectURL(url);

                toast.success(t('employeeManagement.generateLoginAndPasswordSuccessMessage'));
                onCancel(true);
            }
        },
        onError: (error) => {
            const formErrors = error?.response?.data?.errors;
            if (formErrors) {
                Object.entries(formErrors).forEach(([key, value]) => {
                    setError(key as keyof sendLoginsAndPasswordsValue, {
                        message: value?.[0],
                    });
                });
            }
        },
    });

    return (
        <>
            <h3 className={cls.modalHeader}>{users.length === 1 ? t('employeeManagement.sendLoginModalLabel_one') : t('employeeManagement.sendLoginModalLabel')}</h3>
            <p className={cls.modalDescription}>{users.length === 1 ? t('employeeManagement.sendLoginModalDescription_one') : t('employeeManagement.sendLoginModalDescription')}</p>
            <p className={cls.modalNames}><strong>{userNames}</strong></p>
            <form onSubmit={handleSubmit(onSubmit)} noValidate>
                <input {...register('userIds')} type="hidden" />
                <div className={cls.modalButtonWrapper}>
                    <Button className={cls.modalButton} styleType="border" type="button" onClick={onCancel}>
                        {t('employeeManagement.cancel')}
                    </Button>
                    <Button className={cls.modalButton} type="submit">
                        {t('employeeManagement.generate')}
                    </Button>
                </div>
            </form>
        </>
    );
};
